import { Tooltip as MuiTooltip } from '@material-ui/core';
import { HelpOutlineOutlined } from '@material-ui/icons';
import React, { ReactElement, useState } from 'react';
import { usePopoverTooltipStyles, usePopoverTooltipAddonStyles, usePopoverTooltipElementStyles } from './popover-tooltip.styles';
import useTooltipStyles from './tooltip.styles';

interface TooltipProps {
  text: string;
  open?: boolean;
  isInline?: boolean;
  position?: 'bottom' | 'right';
  tooltipTitle?: string;
}

interface PopoverTooltipProps {
  open?: boolean;
  position?: 'bottom' | 'right';
  children: ReactElement;
  messageHeading?: string;
  messageDescription?: string;
  messageDescription2?: string;
  messageSubHeading?: string;
  messageNotes?: string[];
  messageNotes2?: string[];
}

interface PopoverTooltipTitleProps {
  heading?: string;
  description?: string;
  description2?: string;
  subHeading?: string;
  notes?: string[];
  notes2?: string[];
}

const PopoverTooltipTitle: React.FC<PopoverTooltipTitleProps> = ({ heading, description, description2, subHeading, notes, notes2 }) => {
  const popoverTooltipElementStyles = usePopoverTooltipElementStyles();

  return (
    <>
      <div className={popoverTooltipElementStyles.popoverTooltipTextHeading}>
        {heading}
      </div>
      <div className={popoverTooltipElementStyles.popoverTooltipTextContent}>
        {description}
        <b>
          {subHeading}
        </b>
        {notes
          && (
            <ul className={popoverTooltipElementStyles.popoverTooltipTextList}>
              {notes.map((note) => <li key={note}>{note}</li>)}
            </ul>
          )}
        <br />
        <div>
          {description2}
        </div>
        {notes2 && (
          <>
            <br />
            <ul className={popoverTooltipElementStyles.popoverTooltipTextList}>
              {notes2.map((note) => <li key={note}>{note}</li>)}
            </ul>
          </>
        )}
      </div>
    </>
  );
};

export const PopoverTooltip: React.FC<PopoverTooltipProps> = ({ open = false, position = 'bottom', messageHeading, messageDescription, messageDescription2, messageSubHeading, messageNotes, messageNotes2, children }) => {
  const [tooltipOpen, setOpen] = useState(open);
  const tooltipClasses = usePopoverTooltipStyles();
  const tooltipAddonClasses = usePopoverTooltipAddonStyles();

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleOpen = (): void => {
    setOpen(true);
  };

  return (
    <>
      <MuiTooltip
        open={tooltipOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        placement={position}
        enterTouchDelay={0}
        leaveTouchDelay={15000}
        interactive
        title={(
          <>
            <div className={tooltipAddonClasses.mobileClose} onClick={handleClose} role="presentation">X</div>
            <PopoverTooltipTitle heading={messageHeading} description={messageDescription} description2={messageDescription2} subHeading={messageSubHeading} notes={messageNotes} notes2={messageNotes2} />
          </>
        )}
        classes={tooltipClasses}
        arrow
      >
        {children}
      </MuiTooltip>
      {tooltipOpen && (<span className={tooltipAddonClasses.mobileBackdrop} />)}
    </>
  );
};

const Tooltip: React.FC<TooltipProps> = ({ text, isInline = false, open = false, position = 'bottom', tooltipTitle = '' }) => {
  const [tooltipOpen, setOpen] = useState(open);
  const { tooltip__title, tooltip__icon, tooltip__icon__inline, tooltip__title_text } = useTooltipStyles();

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleOpen = (): void => {
    setOpen(true);
  };

  return (
    <MuiTooltip
      data-testid="tooltip"
      open={tooltipOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      title={<span className={`tooltipTitle ${tooltip__title}`}>{text}</span>}
      placement={position}
      enterTouchDelay={50}
      leaveTouchDelay={15000}
      disableTouchListener
    >
      {tooltipTitle
        ? <span className={tooltip__title_text}>{tooltipTitle}</span>
        : <HelpOutlineOutlined className={isInline ? tooltip__icon__inline : tooltip__icon} color="primary" onTouchStart={handleOpen} />}
    </MuiTooltip>
  );
};

export default Tooltip;